import { SolarScreenMap } from '~/machines/solar-funnel/screen-map';
import { ScreenMapCopy } from '~/machines/utils/types';
import { CreateAccountContext, CreateAccountCopy } from '~/screens/create-account';
import {
  CreateAccountGoogleContext,
  CreateAccountGoogleCopy,
} from '~/screens/create-account-google';
import { VnmTermsContext, VnmTermsCopy } from '~/screens/vnm-terms';

interface VnmTermsProps {
  state: string;
  promoCode: string | undefined;
}

export const solarLandscapeSavingsRate = (promoCode?: string, state?: string) => {
  if (state === 'NJ') {
    if (promoCode === 'sl25') return '25%';
    if (promoCode === 'sl30' || promoCode === 'sl30frontline') return '30%';
    return '21%';
  } else if (state === 'MD') {
    return '25%';
  } else {
    return '';
  }
};

const legalSavingBenefitContext = (context: VnmTermsProps) => {
  const { promoCode, state } = context;
  const savingsRate = solarLandscapeSavingsRate(promoCode, state);
  switch (state) {
    case 'MD':
      return `Once you're connected to a solar farm, you'll start receiving credits that reduce your utility bill. You'll save ${savingsRate}* on the portion of your utility bill coming from solar.`;
    case 'NJ':
      return `Once you're connected to a solar farm you'll receive guaranteed savings of ${savingsRate}* on the solar credits applied to your power bill`;
    default:
      return '';
  }
};

const legalSavingBenefitCopy = (context: VnmTermsContext) => {
  const state = context.utility?.state?.abbreviation;
  const promoCode = context.promoCode || context.funnelPacket?.user?.signupPromoCode?.code;
  return legalSavingBenefitContext({ state, promoCode });
};

export const copy: ScreenMapCopy<SolarScreenMap> = {
  user: {
    user: (context: CreateAccountContext): Partial<CreateAccountCopy> => ({
      description: `Community solar is the easiest, most direct way for you to add more clean energy to your local power supply without changing your electric utility. The best part? You can save ${solarLandscapeSavingsRate(
        context.promoCode,
        context.utility?.state?.abbreviation,
      )}* on the solar credits applied to your utility bill.`,
      lmiSavingsPercentageDescription: `*Low-to-moderate income households save ${solarLandscapeSavingsRate(
        context.promoCode,
        context.utility.state.abbreviation,
      )}; all others save 5%.`,
    }),
    google: (context: CreateAccountGoogleContext): Partial<CreateAccountGoogleCopy> => ({
      description: `Community solar is the easiest, most direct way for you to add more clean energy to your local power supply without changing your electric utility. The best part? You can save ${solarLandscapeSavingsRate(
        context.promoCode,
        context.utility?.state?.abbreviation,
      )}* on the solar credits applied to your utility bill.`,
      lmiSavingsPercentageDescription: `*Low-to-moderate income households save ${solarLandscapeSavingsRate(
        context.promoCode,
        context.utility.state.abbreviation,
      )}; all others save 5%.`,
    }),
  },
  legal: {
    review: (context: VnmTermsContext): Partial<VnmTermsCopy> => ({
      bullets: [
        'No extra cost to you',
        'Your power bill will support clean, local energy',
        `${legalSavingBenefitCopy(context)}`,
      ],
      next: 'Looks good to me!',
      savingsBenefit: legalSavingBenefitCopy(context),
      lmiSavingsPercentageDescription: `*Low-to-moderate income households save ${solarLandscapeSavingsRate(
        context.promoCode || context.funnelPacket?.user?.signupPromoCode?.code,
        context.utility.state.abbreviation,
      )}; all others save 5%.`,
    }),
  },
};
